import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import Dropdown from "@/components/Dropdown";
import {
  changeInviteRole,
  deleteTeam,
  getTeam,
  inviteMember,
  listTeamSkills,
  mySkills,
  removeMember,
  removeTeamSkill,
  renameTeam,
  revokeInvite,
  setMemberRole,
  setTeamSkillAccess,
  shareWithTeam,
} from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import type { Access, PendingInvite, Skill, TeamDetail, TeamMember, TeamRole, TeamSkill } from "@/lib/types";
import { useDelayedFlag } from "@/lib/useDelayedFlag";

// Role → chip colours. Theme-aware: neutral fills use a black tint in light mode and a
// white tint in dark, so every badge stays legible against either background.
const ROLE_CLS: Record<string, string> = {
  owner: "bg-primary/15 text-primary",
  admin: "bg-accent/15 text-accent",
  write: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  read: "bg-black/[0.06] text-text/60 dark:bg-white/10",
};

const ROLES: TeamRole[] = ["admin", "write", "read"];

// Distinct avatar gradients — members are coloured deterministically from their identity so a
// team roster reads as a set of people, not a wall of identical circles. (Full literal strings
// so Tailwind's JIT keeps them.)
const AVATAR_GRADIENTS = [
  "from-orange-500 to-amber-400",
  "from-blue-500 to-cyan-400",
  "from-violet-500 to-purple-400",
  "from-emerald-500 to-teal-400",
  "from-pink-500 to-rose-400",
  "from-indigo-500 to-blue-400",
  "from-fuchsia-500 to-pink-400",
  "from-cyan-500 to-sky-400",
];

/** Stable gradient for a member, hashed from a key (their email) so it never shifts between loads. */
function avatarGradient(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return AVATAR_GRADIENTS[h % AVATAR_GRADIENTS.length];
}

/** 1–2 letter initials from a display name (falls back to the email). */
function initials(name: string, email: string): string {
  const src = (name || email).trim();
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

// Shared surface treatment for the elevated (primary) card — reads correctly in both themes,
// since the variables flip with `data-theme` unlike hard-coded white/black overlays.
const CARD = "rounded-2xl border border-[var(--border)] bg-[var(--bg-elev)]";
const CARD_SHADOW = { boxShadow: "var(--shadow-card)" } as const;

// Stable A→Z order so a row never jumps when you change its access (the team-skills API sorts by
// updated_at, which would otherwise shuffle the just-edited skill to the top). Copies, never mutates.
const sortByName = (list: TeamSkill[]): TeamSkill[] => [...list].sort((a, b) => a.name.localeCompare(b.name));

/**
 * Team detail + member management, rendered inline in the dashboard (sidebar stays static).
 *
 * Managers (owner/admin) can rename the team, invite members with a role, change roles,
 * remove members, and delete the team; everyone else sees a read-only view. The members list is
 * the visual focus (elevated card, colour-coded avatars); invite and delete are lighter secondary
 * panels. Styling uses the app's CSS-variable tokens (`--border`, `--bg-elev`, `--shadow-card`)
 * so it looks polished in both light and dark themes.
 *
 * @param slug       Team slug to load and manage.
 * @param onBack     Return to the Teams tab.
 * @param onDeleted  Called after the team is deleted (falls back to `onBack`).
 * @param onRenamed  Called after a successful rename so the parent list can refresh its labels.
 */
export default function TeamManage({
  slug,
  onBack,
  onDeleted,
  onRenamed,
}: {
  slug: string;
  onBack: () => void;
  onDeleted?: () => void;
  onRenamed?: () => void;
}) {
  const { user, accessToken } = useAuth();
  const [team, setTeam] = useState<TeamDetail | null>(null);
  const [email, setEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<TeamRole>("read");
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRoles, setShowRoles] = useState(false);
  const rolesBtn = useRef<HTMLButtonElement>(null);
  // Which detail tab is showing — Members (roster/invites) or Skills (shared skills). Kept in the
  // URL (?tab=…) alongside ?section=teams&team=… so it survives a page refresh and back/forward.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: "members" | "skills" = searchParams.get("tab") === "skills" ? "skills" : "members";
  const setTab = (key: "members" | "skills") =>
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set("tab", key);
        return p;
      },
      { replace: true },
    );
  // Inline team-name editing (managers only).
  const [editing, setEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState("");

  // Shared-skills section. Every team member sees the shared list AND can share skills they own.
  // Per-row change/remove is available on skills you own (any member) or on any skill (managers).
  const [sharedSkills, setSharedSkills] = useState<TeamSkill[]>([]);
  const [myOwned, setMyOwned] = useState<Skill[]>([]);
  const [addSlug, setAddSlug] = useState("");
  const [shareAccess, setShareAccess] = useState<Access>("read");

  // Derived here (before the effects that depend on it) so the manager-only fetch can gate on it.
  // `team` is null until loaded, so guard with optional chaining.
  const myRole = team?.members.find((m) => m.id === user?.id)?.role;
  const isManager = myRole === "owner" || myRole === "admin";

  const load = useCallback(async () => {
    if (!accessToken) return;
    try {
      setTeam(await getTeam(slug, accessToken));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Not found");
    }
  }, [slug, accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  // The shared-skills list is visible to every member, so always load it. Used for the initial
  // load and as the refetch fallback after a share (whose response isn't the team-skills list).
  const loadSharedSkills = useCallback(async () => {
    if (!accessToken) return;
    const list = await listTeamSkills(slug, accessToken).catch(() => [] as TeamSkill[]);
    setSharedSkills(sortByName(list));
  }, [slug, accessToken]);

  useEffect(() => {
    loadSharedSkills();
  }, [loadSharedSkills]);

  // Every member can share skills they own, so the "add" picker is shown to all members —
  // fetch the caller's owned skills for everyone (not just managers).
  useEffect(() => {
    if (!accessToken) return;
    let alive = true;
    mySkills(accessToken)
      .then((mine) => alive && setMyOwned(mine))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [accessToken]);

  // "What roles mean" popover: close on any outside click or Escape (the toggle button's own
  // click is handled by its onClick). Mirrors the share page's permission-details popover.
  useEffect(() => {
    if (!showRoles) return;
    function onDown(e: MouseEvent) {
      if (rolesBtn.current?.contains(e.target as Node)) return;
      setShowRoles(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setShowRoles(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [showRoles]);

  async function changeRole(m: TeamMember, role: TeamRole) {
    if (!accessToken) return;
    setBusy(true);
    setError(null);
    try {
      setTeam(await setMemberRole(slug, m.id, role, accessToken));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  async function kick(m: TeamMember) {
    if (!accessToken) return;
    setBusy(true);
    setError(null);
    try {
      setTeam(await removeMember(slug, m.id, accessToken));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  async function changePendingRole(inv: PendingInvite, role: TeamRole) {
    if (!accessToken) return;
    setBusy(true);
    setError(null);
    try {
      setTeam(await changeInviteRole(slug, inv.id, role, accessToken));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  async function revoke(inv: PendingInvite) {
    if (!accessToken) return;
    setBusy(true);
    setError(null);
    try {
      setTeam(await revokeInvite(slug, inv.id, accessToken));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  // Shared-skills actions. Adding is owner-only (you can't grant a skill you don't own). Changing
  // access on / removing an already-shared skill is open to the skill's owner (so any member can
  // manage what they shared) or a team manager. Each refreshes the list afterwards.
  // The change-access / remove endpoints return the full refreshed list, so apply it directly and
  // skip a second round-trip. The share endpoint returns grant data (a different shape), so that
  // caller resolves to void and we fall back to a single refetch.
  async function runSkill(fn: () => Promise<TeamSkill[] | void>) {
    if (!accessToken) return;
    setBusy(true);
    setError(null);
    try {
      const next = await fn();
      if (next) setSharedSkills(sortByName(next));
      else await loadSharedSkills();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  function addSkill() {
    if (!addSlug) return;
    runSkill(() => shareWithTeam(addSlug, slug, shareAccess, accessToken!).then(() => setAddSlug("")));
  }

  function changeSkillAccess(s: TeamSkill, next: Access) {
    if (s.access === next) return;
    runSkill(() => setTeamSkillAccess(slug, s.slug, next, accessToken!));
  }

  function removeSkill(s: TeamSkill) {
    runSkill(() => removeTeamSkill(slug, s.slug, accessToken!));
  }

  // Defer the loading placeholder so a fast load goes straight to content without a flash.
  const showLoading = useDelayedFlag(!team);

  const back = (
    <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-text/60 transition hover:text-text">
      <BackArrow /> Teams
    </button>
  );

  if (!accessToken) return <p className="text-text/60">Log in to view this team.</p>;
  if (error && !team) return <div>{back}<p className="mt-4 text-text/60">{error}</p></div>;
  if (!team) return showLoading ? <div>{back}<p className="mt-4 text-text/50">Loading…</p></div> : null;

  const isSolo = team.members.length === 1;

  // Ascending A→Z ordering for the roster and pending invites (shared skills are already
  // sorted in loadSharedSkills). Copies, so we never mutate the source arrays.
  const sortedMembers = [...team.members].sort((a, b) =>
    (a.display_name || a.email).localeCompare(b.display_name || b.email),
  );
  const sortedInvites = [...team.pending_invites].sort((a, b) => a.email.localeCompare(b.email));

  // Picker options: my own skills not already shared with this team. A skill I own that's
  // shared shows up in `sharedSkills` with owned=true, so exclude those.
  const sharedOwnedSlugs = new Set(sharedSkills.filter((s) => s.owned).map((s) => s.slug));
  const availableSkills = myOwned.filter((s) => !sharedOwnedSlugs.has(s.slug));

  // Split the shared list into "yours" (skills you shared/own) and the rest, so a member can see
  // what they contributed alongside everyone else's. A member can manage rows they own; a manager
  // can manage any row.
  const myShared = sharedSkills.filter((s) => s.owned);
  const othersShared = sharedSkills.filter((s) => !s.owned);
  const canManage = (s: TeamSkill) => isManager || s.owned;

  const skillRow = (s: TeamSkill) => (
    <li
      key={s.slug}
      className="flex items-center gap-3 p-4 text-sm transition first:rounded-t-2xl last:rounded-b-2xl hover:bg-black/[0.02] dark:hover:bg-white/[0.02]"
    >
      {/* Name + avatar link to the skill page so a member can open a shared skill to use it. */}
      <Link to={`/skill/${s.slug}`} className="group flex min-w-0 flex-1 items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-xs font-semibold text-primary">
          {s.name.charAt(0).toUpperCase()}
        </span>
        <span className="min-w-0">
          <span className="block truncate font-medium transition-colors group-hover:text-primary">{s.name}</span>
          <span className="block truncate text-xs text-text/40">
            {s.owned ? "Shared by you" : `Shared by ${s.owner_name ?? "another member"}`}
          </span>
        </span>
      </Link>
      {canManage(s) ? (
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          <div className="inline-flex rounded-md border border-[var(--border)] p-0.5 text-[11px] font-medium">
            {(["read", "write"] as Access[]).map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => changeSkillAccess(s, a)}
                disabled={busy}
                aria-pressed={s.access === a}
                title={`Set ${a} access`}
                className={`rounded px-2 py-0.5 capitalize transition disabled:opacity-50 ${
                  s.access === a
                    ? a === "write"
                      ? "bg-green-500/20 text-green-400"
                      : "bg-black/10 text-text/70 dark:bg-white/15"
                    : "text-text/40 hover:text-text/70"
                }`}
              >
                {a}
              </button>
            ))}
          </div>
          <button
            onClick={() => removeSkill(s)}
            disabled={busy}
            aria-label="Remove from team"
            title="Remove from team"
            className="inline-flex items-center gap-1.5 rounded-lg border border-red-500/30 px-3 py-2 text-sm font-medium text-red-500 transition hover:border-red-500/50 hover:bg-red-500/10 disabled:opacity-50 dark:text-red-400"
          >
           Remove <XIcon />
          </button>
        </div>
      ) : (
        // Read-only for members on skills they don't own.
        <span className={`ml-auto shrink-0 rounded-md px-2 py-0.5 text-xs font-medium capitalize ${ROLE_CLS[s.access]}`}>
          {s.access}
        </span>
      )}
    </li>
  );

  function startEdit() {
    if (!team) return;
    setNameDraft(team.name);
    setEditing(true);
    setError(null);
  }

  async function onRename(e: React.FormEvent) {
    e.preventDefault();
    if (!team) return;
    const name = nameDraft.trim();
    if (!name || name === team.name) {
      setEditing(false);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setTeam(await renameTeam(slug, name, accessToken!));
      setEditing(false);
      onRenamed?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to rename team");
    } finally {
      setBusy(false);
    }
  }

  async function onDelete() {
    if (!accessToken) return;
    setBusy(true);
    setError(null);
    try {
      await deleteTeam(slug, accessToken!);
      (onDeleted ?? onBack)();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to delete team");
      setBusy(false);
    }
  }

  async function onInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    setError(null);
    setMsg(null);
    try {
      await inviteMember(slug, email.trim(), inviteRole, accessToken!);
      setMsg(`Invited ${email.trim()} as ${inviteRole} — they must accept.`);
      setEmail("");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to invite");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="w-full">
      {/* ── Hero: team identity — plain header, no card ─────────────────────── */}
      <header className="flex items-start gap-4">
        <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-accent text-2xl font-bold text-white shadow-sm">
          {team.name.charAt(0).toUpperCase()}
        </span>
        <div className="min-w-0 flex-1">
          {editing ? (
            <form onSubmit={onRename} className="flex flex-wrap items-center gap-2">
              <input
                autoFocus
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onKeyDown={(e) => e.key === "Escape" && setEditing(false)}
                maxLength={80}
                aria-label="Team name"
                className="min-w-0 flex-1 rounded-xl border border-[var(--border)] bg-transparent px-3 py-2 text-xl font-bold tracking-tight outline-none transition focus:border-primary"
              />
              <button
                disabled={busy || !nameDraft.trim()}
                className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-hover disabled:opacity-50"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => setEditing(false)}
                disabled={busy}
                className="rounded-xl border border-[var(--border)] px-4 py-2 text-sm transition hover:bg-black/[0.04] disabled:opacity-50 dark:hover:bg-white/[0.06]"
              >
                Cancel
              </button>
            </form>
          ) : (
            <div className="flex items-center gap-2.5">
              <h1 className="truncate font-display text-[1.7rem] font-bold leading-tight tracking-tight">{team.name}</h1>
              {isManager && (
                <button
                  onClick={startEdit}
                  title="Rename team"
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-[var(--border)] px-2.5 py-1.5 text-xs font-medium text-text/70 transition hover:border-primary/40 hover:text-text"
                >
                  <PencilIcon /> Rename
                </button>
              )}
            </div>
          )}
          <div className="mt-2.5 flex flex-wrap items-center gap-x-2.5 gap-y-1.5 text-sm text-text/55">
            <span>
              {team.members.length} member{team.members.length === 1 ? "" : "s"}
            </span>
            {myRole && (
              <>
                <span aria-hidden className="text-text/25">·</span>
                <span className="inline-flex items-center gap-1.5">Your role <RoleTag role={myRole} /></span>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ── Tabs: Members · Skills (underline style) ────────────────────────── */}
      <div
        role="tablist"
        aria-label="Team sections"
        className="mt-6 flex items-center gap-6 border-b border-[var(--border)]"
      >
        {([
          ["members", "Members"],
          ["skills", "Skills"],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={`-mb-px border-b-2 px-1 pb-2.5 text-sm transition ${
              tab === key
                ? "border-primary font-semibold text-text"
                : "border-transparent font-medium text-text/55 hover:text-text"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Members tab: invite · pending · roster ──────────────────────────── */}
      {tab === "members" && (
        <div className="mt-6 space-y-7">
        {/* Add to team */}
        {isManager ? (
          <section>
            <SectionHeader title="Add to team" hint="They join the team once they accept the invite." />
            <div className="mt-3.5">
              <form onSubmit={onInvite} className="flex flex-wrap items-center gap-2.5">
                <div className="relative min-w-[12rem] flex-1">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text/35">
                    <MailIcon />
                  </span>
                  <input
                    type="email"
                    placeholder="email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-lg border border-[var(--border)] bg-[var(--bg-elev)] py-2 pl-9 pr-3 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <Dropdown
                  value={inviteRole}
                  onChange={(v) => setInviteRole(v as TeamRole)}
                  ariaLabel="Invite role"
                  className="w-28"
                  options={ROLES.map((r) => ({ value: r, label: r }))}
                />
                <button
                  disabled={busy || !email.trim()}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-hover disabled:opacity-50 disabled:shadow-none"
                >
                  <UserPlusIcon /> Invite
                </button>
              </form>
              {msg && <p className="mt-2.5 text-sm text-accent">{msg}</p>}
            </div>
          </section>
        ) : (
          <p className="text-sm text-text/45">Only the owner or an admin can manage members.</p>
        )}

        {/* Pending invites */}
        {team.pending_invites.length > 0 && (
          <section>
            <SectionHeader title="Pending invites" count={team.pending_invites.length} hint="Waiting for these people to accept." />
            <ul className={`mt-3.5 divide-y divide-[var(--border)] ${CARD}`} style={CARD_SHADOW}>
              {sortedInvites.map((inv) => (
                <li key={inv.id} className="flex items-center gap-3 p-4 text-sm">
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br text-xs font-semibold text-white opacity-70 ${avatarGradient(inv.email)}`}>
                    {initials("", inv.email)}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{inv.email}</span>
                    <span className="block truncate text-xs font-medium text-amber-600 dark:text-amber-400">Pending invite</span>
                  </span>
                  {isManager ? (
                    <div className="ml-auto flex shrink-0 items-center gap-1.5">
                      <Dropdown
                        value={inv.role}
                        onChange={(v) => changePendingRole(inv, v as TeamRole)}
                        disabled={busy}
                        ariaLabel="Invite role"
                        align="right"
                        className="w-28"
                        options={ROLES.map((r) => ({ value: r, label: r }))}
                      />
                      <button
                        onClick={() => revoke(inv)}
                        disabled={busy}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-red-500/30 px-3 py-2 text-sm font-medium text-red-500 transition hover:border-red-500/50 hover:bg-red-500/10 disabled:opacity-50 dark:text-red-400"
                      >
                        Revoke <XIcon />
                      </button>
                    </div>
                  ) : (
                    <span className={`ml-auto shrink-0 rounded-md px-2 py-0.5 text-xs font-medium ${ROLE_CLS[inv.role] ?? ROLE_CLS.read}`}>
                      {inv.role}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Members */}
        <section>
          <SectionHeader
            title="Members"
            count={team.members.length}
            action={
              <div className="relative">
                <button
                  ref={rolesBtn}
                  type="button"
                  onClick={() => setShowRoles((s) => !s)}
                  aria-expanded={showRoles}
                  className="inline-flex items-center gap-1 text-xs text-text/50 transition hover:text-text/80"
                >
                  <InfoIcon /> What roles mean
                </button>
                {showRoles && (
                  <div
                    role="dialog"
                    className="absolute right-0 top-full z-30 mt-2 w-[290px] space-y-1.5 rounded-xl border border-[var(--border)] bg-[var(--bg-elev)] p-3.5 text-xs text-text/60"
                    style={{ boxShadow: "var(--shadow-card)" }}
                  >
                    {/* caret pointing up at the toggle */}
                    <span className="absolute -top-1.5 right-4 h-3 w-3 rotate-45 border-l border-t border-[var(--border)] bg-[var(--bg-elev)]" />
                    <div className="flex items-center gap-2"><RoleTag role="admin" /> manages members &amp; roles</div>
                    <div className="flex items-center gap-2"><RoleTag role="write" /> edits &amp; versions shared skills</div>
                    <div className="flex items-center gap-2"><RoleTag role="read" /> views &amp; installs shared skills</div>
                    <p className="pt-1 text-text/40">A member&apos;s role caps their access to any skill shared with the team.</p>
                  </div>
                )}
              </div>
            }
          />

          <ul className={`mt-3.5 divide-y divide-[var(--border)] ${CARD}`} style={CARD_SHADOW}>
            {sortedMembers.map((m) => {
              const isMe = m.id === user?.id;
              return (
              <li
                key={m.id}
                className={`flex items-center gap-3 p-4 text-sm transition first:rounded-t-2xl last:rounded-b-2xl ${
                  isMe ? "bg-primary/[0.06] dark:bg-primary/[0.10]" : "hover:bg-black/[0.02] dark:hover:bg-white/[0.02]"
                }`}
              >
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br text-xs font-semibold text-white ${avatarGradient(m.email)}`}>
                  {initials(m.display_name, m.email)}
                </span>
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5">
                    <span className="truncate font-medium">{m.display_name || m.email}</span>
                    {isMe && (
                      <span className="shrink-0 rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold text-primary">You</span>
                    )}
                  </span>
                  <span className="block truncate text-xs text-text/40">{m.email}</span>
                </span>
                {isManager && m.role !== "owner" ? (
                  <div className="ml-auto flex shrink-0 items-center gap-1.5">
                    <Dropdown
                      value={m.role}
                      onChange={(v) => changeRole(m, v as TeamRole)}
                      disabled={busy}
                      ariaLabel="Member role"
                      align="right"
                      className="w-28"
                      options={ROLES.map((r) => ({ value: r, label: r }))}
                    />
                    <button
                      onClick={() => kick(m)}
                      disabled={busy}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-red-500/30 px-3 py-2 text-sm font-medium text-red-500 transition hover:border-red-500/50 hover:bg-red-500/10 disabled:opacity-50 dark:text-red-400"
                    >
                     Remove <XIcon />
                    </button>
                  </div>
                ) : (
                  <span className={`ml-auto shrink-0 rounded-md px-2 py-0.5 text-xs font-medium ${ROLE_CLS[m.role] ?? ROLE_CLS.read}`}>
                    {m.role}
                  </span>
                )}
              </li>
              );
            })}
          </ul>

          {isSolo && isManager && team.pending_invites.length === 0 && (
            <div className="mt-3 flex items-center gap-3 rounded-2xl border border-dashed border-[var(--border)] px-4 py-3.5 text-sm text-text/55">
              <PeopleIcon />
              <span>It&apos;s just you so far — invite teammates to collaborate on skills shared with the team.</span>
            </div>
          )}
        </section>
        </div>
      )}

      {/* ── Skills tab: shared-skills list (any member can add / manage own) ─── */}
      {tab === "skills" && (
        <div className="mt-6 space-y-7">
        {/* Shared skills — every team member sees the list and can share skills they own. Managers
            can manage any row; a member can manage the rows they shared. */}
        <section>
          <SectionHeader
            title="Shared skills"
            count={sharedSkills.length}
            hint="Skills shared with this team. Everyone can use them, and any member can share skills they own."
          />

          <div className="mt-3.5">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (addSlug && !busy) addSkill();
                }}
                className="flex flex-wrap items-center gap-2.5"
              >
                <Dropdown
                  value={addSlug}
                  onChange={setAddSlug}
                  ariaLabel="Skill to share"
                  placeholder={availableSkills.length ? "Choose a skill you own" : "No skills to add"}
                  disabled={busy || availableSkills.length === 0}
                  clearable
                  searchable
                  className="min-w-[12rem] flex-1"
                  options={availableSkills.map((s) => ({
                    value: s.slug,
                    label: s.name,
                    icon: (
                      <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/15 text-[11px] font-semibold text-primary">
                        {s.name.charAt(0).toUpperCase()}
                      </span>
                    ),
                  }))}
                />
                <Dropdown
                  value={shareAccess}
                  onChange={(v) => setShareAccess(v as Access)}
                  ariaLabel="Access level"
                  className="w-28"
                  options={[
                    { value: "read", label: "Read" },
                    { value: "write", label: "Write" },
                  ]}
                />
                <button
                  type="submit"
                  disabled={busy || !addSlug}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-hover disabled:opacity-50 disabled:shadow-none"
                >
                  <PlusIcon /> Share
                </button>
              </form>
              {myOwned.length === 0 ? (
                <p className="mt-2 text-xs text-text/45">You don&apos;t own any skills to share yet.</p>
              ) : (
                availableSkills.length === 0 && (
                  <p className="mt-2 text-xs text-text/45">All your skills are already shared with this team.</p>
                )
              )}
            </div>

          {sharedSkills.length === 0 ? (
            <div className="mt-3.5 flex items-center gap-3 rounded-2xl border border-dashed border-[var(--border)] px-4 py-3.5 text-sm text-text/55">
              <PeopleIcon />
              <span>No skills are shared with this team yet.</span>
            </div>
          ) : (
            <div className="mt-3.5 space-y-5">
              {myShared.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text/45">Shared by you</p>
                  <ul className={`divide-y divide-[var(--border)] ${CARD}`} style={CARD_SHADOW}>
                    {myShared.map(skillRow)}
                  </ul>
                </div>
              )}
              {othersShared.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text/45">
                    {myShared.length > 0 ? "Shared by others" : "Shared with the team"}
                  </p>
                  <ul className={`divide-y divide-[var(--border)] ${CARD}`} style={CARD_SHADOW}>
                    {othersShared.map(skillRow)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </section>
        </div>
      )}

      {/* ── Danger zone: delete team — constant (both tabs), managers only ───── */}
      {isManager && (
        <section className="mt-7 rounded-2xl border border-red-500/20 bg-red-500/[0.03] p-5">
            <h2 className="font-display text-sm font-semibold text-red-500 dark:text-red-400">Delete team</h2>
            <p className="mt-1.5 text-xs text-text/55">
              Permanently deletes <b className="text-text/80">{team.name}</b>, its pending invites,
              and removes it from any skills shared with it. The skills themselves are not deleted.
              This cannot be undone.
            </p>
            {!confirmDelete ? (
              <button
                onClick={() => setConfirmDelete(true)}
                className="mt-3 rounded-lg border border-red-500/40 px-4 py-2 text-sm text-red-500 transition hover:bg-red-500/10 dark:text-red-400"
              >
                Delete team
              </button>
            ) : (
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  onClick={onDelete}
                  disabled={busy}
                  className="rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white transition hover:opacity-90 disabled:opacity-50"
                >
                  {busy ? "Deleting…" : "Yes, delete permanently"}
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  disabled={busy}
                  className="rounded-lg border border-[var(--border)] px-4 py-2 text-sm transition hover:bg-black/[0.04] disabled:opacity-50 dark:hover:bg-white/[0.06]"
                >
                  Cancel
                </button>
              </div>
            )}
        </section>
      )}

      {error && <p className="mt-4 text-sm text-red-500 dark:text-red-400">{error}</p>}
    </div>
  );
}

/** Section title + optional count pill and a right-aligned action. Gives the page a consistent,
 *  scannable rhythm across the Add / Pending / Members blocks. */
function SectionHeader({
  title,
  count,
  hint,
  action,
}: {
  title: string;
  count?: number;
  hint?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h2 className="font-display text-[0.95rem] font-semibold tracking-tight text-text">{title}</h2>
          {count != null && (
            <span className="rounded-full bg-black/[0.05] px-2 py-0.5 text-xs font-medium text-text/50 dark:bg-white/10">
              {count}
            </span>
          )}
        </div>
        {hint && <p className="mt-0.5 text-xs text-text/45">{hint}</p>}
      </div>
      {action && <div className="shrink-0 pt-0.5">{action}</div>}
    </div>
  );
}

/** Small colour-coded role pill used in the legend and as a member badge. */
function RoleTag({ role }: { role: string }) {
  return (
    <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${ROLE_CLS[role] ?? ROLE_CLS.read}`}>
      {role}
    </span>
  );
}

function XIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 6L2 7" />
    </svg>
  );
}

function UserPlusIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M19 8v6M22 11h-6" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function BackArrow() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" />
    </svg>
  );
}

function PeopleIcon() {
  return (
    <svg width="18" height="18" className="shrink-0 text-text/40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
